import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, computed, DestroyRef, effect, HostListener, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, ParamMap, Router, RouterModule } from '@angular/router';
import { CommitteeAuditLogComponent } from '@app/modules/collaboration/committees/components/committee-audit-log/committee-audit-log.component';
import { MeetingAuditLogComponent } from '@app/modules/collaboration/meetings/components/meeting-audit-log/meeting-audit-log.component';
import { ActivityLogComponent } from '@app/modules/operations/project-definition/components/activity-log/activity-log.component';
import { HealthMetricTimeRangeComponent } from '@app/modules/reports/health-metrics/components/health-metric-time-range/health-metric-time-range.component';
import { ProjectStageInfoPipe } from '@app/shared/pipes/project-stage-info.pipe';
import { MetaService } from '@app/shared/services/meta.service';
import { ProjectService } from '@app/shared/services/project.service';
import { UserService } from '@app/shared/services/user.service';
import { hasHealthMetricDashboard } from '@app/shared/utils/helper';
import { environment } from '@environments/environment';
import { Project, ProjectSummary, ProjectSummaryCategory } from 'lfx-pcc';
import { orderBy } from 'lodash';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';
import { filter, finalize } from 'rxjs';

import { CreateProjectComponent } from '../create-project/create-project.component';

@Component({
  selector: 'lfx-common-header',
  standalone: true,
  imports: [
    CommonModule,
    TreeSelectModule,
    ReactiveFormsModule,
    SkeletonModule,
    TooltipModule,
    RouterModule,
    MessageModule,
    ActivityLogComponent,
    ButtonModule,
    HealthMetricTimeRangeComponent,
    DrawerModule,
    CommitteeAuditLogComponent,
    MeetingAuditLogComponent,
    ProjectStageInfoPipe,
  ],
  templateUrl: './common-header.component.html',
  styleUrls: ['./common-header.component.scss'],
})
export class CommonHeaderComponent {
  public projectID = input<string | null>(null);
  public currentProjectID = input<string | null>(null);

  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private dialogService = inject(DialogService);
  private metaService = inject(MetaService);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  // Convert router observables to signals (call in injection context)
  private queryParams = toSignal(this.activatedRoute.queryParamMap);
  private routerNavigationEnd = toSignal(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)));

  public project = signal<Project | null>(null);
  public foundation = signal<ProjectSummary | null>(null);
  public childProjects = signal<TreeNode<Project | ProjectSummary>[]>([]);
  public isLoading = signal<boolean>(false);
  public showAuditLog = signal<boolean>(false);
  public isHealthMetrics = signal<boolean>(this.router.url.includes('health-metrics'));
  public showHealthTimeRange = signal<boolean>(false);
  public pageTitle = signal<string>('');
  public routerUrl = signal<string>(this.router.url);
  public meetingId = signal<string | null>(null);
  public isPastMeeting = signal<boolean>(false);
  public isCloneMeeting = signal<boolean>(false);

  public meetingTopic = this.metaService.meetingTopicSig;
  public permissions = computed(() => this.userService.permissionsSig());
  public isFavorite = computed(() => {
    const currentProject = this.project();
    if (!currentProject) return false;
    return this.userService.favoriteProjectsSig().some((p) => p.ID === currentProject.ID);
  });
  public hasCommitteeAuditLog = computed(() => this.routerUrl().includes('collaboration/committees'));
  public hasProjectAuditLog = computed(() => this.routerUrl().includes('operations/project-definition') && this.permissions()?.project_audits?.view_all);
  public hasMeetingAuditLog = computed(
    () => this.routerUrl().includes('collaboration/meetings/manage-meeting') && !!this.meetingId() && !this.isCloneMeeting()
  );
  public hasShowAuditLog = computed(() => this.hasCommitteeAuditLog() || this.hasProjectAuditLog() || this.hasMeetingAuditLog());
  public canCreateProjects = computed(() => this.permissions()?.projects?.create && this.userService.lfStaffUserSig());
  public escalateReviewLink = computed(() => {
    const currentProject = this.project();
    return currentProject
      ? `mailto:formation@linuxfoundation.org?subject=Status of project ${currentProject.Name} (${currentProject.ID})`
      : 'mailto:formation@linuxfoundation.org?subject=Status of project';
  });

  public form: FormGroup;

  public constructor() {
    this.form = this.fb.group({
      project: [''],
    });

    // Initialize page title
    this.pageTitle.set(this.metaService.title);

    effect(
      () => {
        const currentProject = this.projectService.currentProjectSig();
        if (currentProject) {
          this.project.set(currentProject);
          this.loadFoundationProject(currentProject);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const projectId = this.projectID();
        if (projectId) {
          this.isHealthMetrics.set(this.router.url.includes('health-metrics'));
          this.pageTitle.set(this.metaService.title);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const params = this.queryParams();
        if (params) {
          this.updateMeetingAuditLogParams(params);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const event = this.routerNavigationEnd();
        if (event) {
          this.routerUrl.set(this.router.url);
          const isNowHealthMetrics = this.router.url.includes('health-metrics');
          if (isNowHealthMetrics !== this.isHealthMetrics()) {
            this.isHealthMetrics.set(isNowHealthMetrics);
            const currentProject = this.project();
            if (currentProject) {
              this.loadFoundationProject(currentProject);
            }
          }
        }
      },
      { allowSignalWrites: true }
    );

    this.form.controls['project'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project) => {
      this.onChangeProject(project);
    });
  }

  @HostListener('window:scroll', ['$event'])
  public onWindowScroll(): void {
    this.pageTitle.set(this.metaService.title);
    this.showHealthTimeRange.set(this.isHealthMetrics() && window.scrollY >= 110);
  }

  public toggleAuditLog(): void {
    this.showAuditLog.update((value) => !value);
  }

  public onChangeProject(project: TreeNode<Project | ProjectSummary>): void {
    if (this.isHealthMetrics()) {
      this.router.navigate([`/project/${project.key}/reports/health-metrics`], { onSameUrlNavigation: 'reload' });
    } else {
      this.router.navigate([`/project/${project.key}`], { onSameUrlNavigation: 'reload' });
    }
  }

  public onShowProjects(event: any): void {
    setTimeout(() => {
      const overlay = event.overlay as HTMLElement;

      if (!overlay) {
        return;
      }

      const selectedNode = overlay.querySelector('[aria-selected="true"]') as HTMLElement;

      if (!selectedNode) {
        return;
      }

      const scrollContainer = overlay.querySelector('.p-tree-container') || overlay;
      const containerRect = scrollContainer.getBoundingClientRect();
      const nodeRect = selectedNode.getBoundingClientRect();

      const isNearTop = Math.abs(nodeRect.top - containerRect.top) < 50;
      const isInViewport = nodeRect.top >= containerRect.top && nodeRect.top <= containerRect.bottom;

      // Only scroll if not near the top AND not in viewport
      if (!isNearTop && !isInViewport) {
        selectedNode.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 250);
  }

  public onCreate(): void {
    const currentProject = this.project();
    this.dialogService
      .open(CreateProjectComponent, {
        header: 'Add Project',
        width: '70%',
        closable: true,
        modal: true,
        showHeader: true,
        dismissableMask: true,
        data: {
          parent: currentProject,
        },
      })
      ?.onClose.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((project) => {
        if (project) {
          this.router.navigate([`/project/${project.ID}`], { onSameUrlNavigation: 'reload' });
        }
      });
  }

  public onToggleFavorite(): void {
    const currentProject = this.project();
    if (!currentProject) return;

    if (!this.isFavorite()) {
      this.userService.favoriteProjects.push({
        ID: currentProject.ID,
        Name: currentProject.Name,
        Slug: currentProject.Slug,
      });
    } else {
      this.userService.favoriteProjects = this.userService.favoriteProjects.filter((project) => project.ID !== currentProject.ID);
    }

    // Sync signal immediately for instant UI feedback
    this.userService.favoriteProjectsSig.set([...this.userService.favoriteProjects]);

    this.userService.updateUserFavoriteProjects();
  }

  private loadFoundationProject(currentProject: Project): void {
    this.isLoading.set(true);
    this.childProjects.set([]);

    let foundationProject = currentProject.ParentHierarchy || currentProject;

    while (
      foundationProject.ParentHierarchy &&
      foundationProject.ParentHierarchy.ID &&
      foundationProject.ParentHierarchy.ID !== environment.linuxFoundationSFID
    ) {
      foundationProject = foundationProject.ParentHierarchy;
    }

    let summaryParams: HttpParams = new HttpParams({
      fromObject: { $filter: `id eq ${foundationProject.ID}`, orderBy: 'name', view: 'pcc', pageSize: '9999' },
    });

    if (this.isHealthMetrics()) {
      summaryParams = summaryParams.delete('$filter').append('excludeCategories', 'true');
    }

    this.projectService
      .getProjectSummary(summaryParams)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe((projects) => {
        const foundProject = projects.find((p) => p.ID === foundationProject.ID) as ProjectSummary;
        const foundationData = foundProject || (currentProject as unknown as ProjectSummary);
        this.foundation.set(foundationData);

        const treeNodes = this.buildChildProjects(projects, currentProject, foundationData);
        this.childProjects.set(treeNodes);

        // Update form value
        this.form.controls['project'].setValue(
          {
            key: currentProject.ID,
            label: currentProject.Name,
            icon: 'pi pi-fw pi-inbox',
            type: 'project',
            data: currentProject,
            leaf: currentProject.Projects && currentProject.Projects.length > 0,
            children: [],
          },
          { emitEvent: false }
        );
      });
  }

  private buildChildProjects(
    projects: ProjectSummary[],
    currentProject: Project,
    foundationProject: Project | ProjectSummary
  ): TreeNode<Project | ProjectSummary>[] {
    if (this.isHealthMetrics()) {
      return this.mapHealthMetricProjectsOnly(projects);
    }

    const childNodes: TreeNode<Project | ProjectSummary>[] = [];

    projects.forEach((project) => {
      const pathToCurrentProject = this.findProjectAndPath(project.Projects || [], currentProject.ID);
      const categories: TreeNode<Project | ProjectSummary>[] = [];

      if (project.Categories?.length > 0) {
        project.Categories.forEach((c: ProjectSummaryCategory) => {
          if (c.Name && c.Name.trim() !== '' && c.Name !== 'None' && c.Projects && c.Projects.length > 0) {
            categories.push({
              key: `${c.Name}${project.ID}`,
              label: c.Name,
              selectable: false,
              icon: 'fa-light fa-folder text-yellow font-semibold text-xs',
              style: 'font-weight: bold',
              type: 'category',
              data: c as unknown as ProjectSummary,
              leaf: c.Projects && c.Projects.length > 0,
              children: c.Projects && c.Projects.length ? this.mapTreeNodes(c.Projects as ProjectSummary[], pathToCurrentProject || []) : [],
            });
          }

          if ((c.Name === 'None' || c.Name.trim() === '') && c.Projects && c.Projects.length > 0) {
            project.Projects = orderBy([...c.Projects, ...((project.Projects as ProjectSummary[]) || [])], ['Name'], ['asc']);
          }
        });
      }

      childNodes.push({
        key: project.ID,
        label: project.Name,
        type: 'project',
        data: project,
        expanded: project.ID === foundationProject.ID,
        leaf: project.Projects && project.Projects.length > 0,
        children:
          project.Projects && project.Projects.length
            ? [...categories, ...this.mapTreeNodes((project.Projects as ProjectSummary[]) || [], pathToCurrentProject || [])]
            : [],
      });
    });

    // Move The Linux Foundation to the top
    return childNodes.sort((a, b) => {
      if (a?.label === 'The Linux Foundation') return -1;
      if (b?.label === 'The Linux Foundation') return 1;
      return 0;
    });
  }

  private mapTreeNodes(projects: ProjectSummary[], path: ProjectSummary[]): TreeNode<Project | ProjectSummary>[] {
    if (this.isHealthMetrics()) {
      return this.mapHealthMetricProjectsOnly(projects);
    }

    return projects.map((p) => {
      const categories: TreeNode<Project | ProjectSummary>[] = [];

      if (p.Categories?.length) {
        p.Categories.forEach((c: ProjectSummaryCategory) => {
          if (c.Name && c.Name.trim() !== '' && c.Name !== 'None' && c.Projects && c.Projects.length > 0) {
            categories.push({
              key: `${c.Name}${p.ID}`,
              label: c.Name,
              type: 'category',
              selectable: false,
              icon: 'fa-light fa-folder text-yellow font-semibold text-xs',
              style: 'font-weight: bold',
              data: c as unknown as ProjectSummary,
              leaf: c.Projects && c.Projects.length > 0,
              children: c.Projects && c.Projects.length ? this.mapTreeNodes(c.Projects as ProjectSummary[], path || []) : [],
            });
          }

          if ((c.Name === 'None' || c.Name.trim() === '') && c.Projects && c.Projects.length > 0) {
            p.Projects = orderBy([...c.Projects, ...((p.Projects as ProjectSummary[]) || [])], ['Name'], ['asc']);
          }
        });
      }

      return {
        key: p.ID,
        label: p.Name,
        type: 'project',
        data: p,
        expanded: false,
        leaf: p.Projects && p.Projects.length > 0,
        children: p.Projects && p.Projects.length ? [...categories, ...this.mapTreeNodes(p.Projects, path)] : [...categories],
      };
    });
  }

  private mapHealthMetricProjectsOnly(projects: ProjectSummary[] | Project[]): TreeNode<Project | ProjectSummary>[] {
    return (projects as ProjectSummary[])
      .filter((p: ProjectSummary) => hasHealthMetricDashboard(p))
      .map((p) => ({
        key: p.ID,
        label: p.Name,
        type: 'project',
        data: p,
        expanded: false,
        leaf: (p.Projects && p.Projects.some((child) => hasHealthMetricDashboard(child))) || undefined,
        children:
          p.Projects && p.Projects.some((child) => hasHealthMetricDashboard(child))
            ? this.mapHealthMetricProjectsOnly(p.Projects as ProjectSummary[])
            : undefined,
      }));
  }

  private findProjectAndPath(projects: ProjectSummary[], currentProjectId: string): ProjectSummary[] | null {
    const path: ProjectSummary[] = [];

    function dfs(projectList: ProjectSummary[]): boolean {
      for (const project of projectList) {
        path.push(project);

        if (project.ID === currentProjectId) {
          return true;
        }

        if (project.Projects && dfs(project.Projects)) {
          return true;
        }

        path.pop();
      }

      return false;
    }

    return dfs(projects) ? path : null;
  }

  private updateMeetingAuditLogParams(params: ParamMap): void {
    if (this.router.url.includes('collaboration/meetings/manage-meeting')) {
      this.meetingId.set(params.get('id') || null);
      this.isPastMeeting.set(params.get('isPast') === 'true');
      this.isCloneMeeting.set(params.get('clone') === 'true');
    } else {
      this.meetingId.set(null);
      this.isPastMeeting.set(false);
      this.isCloneMeeting.set(false);
    }
  }
}
